/**
 * Pure file operations — no React dependencies.
 */

const { REACT_APP_ENCRYPT_KEY } = process.env;
const encrypter = require("json-file-encrypt");
const encryptionKey = new encrypter.encryptor(REACT_APP_ENCRYPT_KEY);

/**
 * Downloads form data as an encrypted .txt file.
 * @param {object} docData - The form document object
 * @param {string} formName - The form name (fallback for filename)
 * @returns {Promise<Blob>} The blob that was downloaded
 */
export async function downloadFile(docData, formName) {
  let date = new Date();
  date = date.toString().slice(0, -19);

  const fileName = docData.docName
    ? `${docData.docName} || ${date}`
    : docData.owner && docData.owner[0] && (docData.owner[0].firstName || docData.owner[0].lastName)
      ? `${docData.owner[0].firstName} ${docData.owner[0].lastName} || ${date}`
      : `${formName} || ${date}`;

  const json = JSON.stringify(docData);
  const encryptedJson = encryptionKey.encrypt(json);

  const blob = new Blob([encryptedJson], { type: "text/plain" });
  const href = await URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.download = `${fileName}_encrypted.txt`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  return blob;
}

/**
 * Builds the payload for saving a draft or template.
 * @param {object} docData - The form document object
 * @param {object} options
 * @param {string} options.taskId - Current task ID (or null)
 * @param {string} options.formId - Form ID
 * @param {string} options.formName - Form name from DB
 * @param {string} options.status - "טיוטה" or "תבנית"
 * @param {string} [options.draftName] - Template name override
 * @returns {object} The draft payload ready to POST
 */
export function buildDraftPayload(docData, options) {
  const { taskId, formId, formName, status, draftName } = options;

  // Filter out File/FileList objects before stringifying (they can't be saved)
  const cleanJson = JSON.stringify(docData, (_key, value) =>
    value instanceof File || value instanceof FileList ? undefined : value
  );
  const encryptedJson = encryptionKey.encrypt(cleanJson);
  const shortName = docData.docName
    ? docData.docName
    : `${docData.owner[0].firstName} ${docData.owner[0].lastName}`;
  const newTaskName = `${formName}- ${shortName}`;

  const sendInput = {
    taskId: taskId == null ? "" : taskId,
    newTxtContent: encryptedJson,
    formId: formId,
    taskName: newTaskName,
    status: status,
  };

  if (status === "תבנית") {
    sendInput.taskName = draftName;
  }

  return sendInput;
}

/**
 * Returns the encryption key instance for use by other modules.
 */
export function getEncryptionKey() {
  return encryptionKey;
}
